// sparklines.jsx — temp curve + precip probability bars above the hourly strip.
// DPR-aware canvas; redraws on resize / data change.

import { useEffect, useRef } from 'preact/hooks';

export function HourlySparklines({ hourly, brand, density }) {
  const tempRef = useRef(null);
  const popRef  = useRef(null);

  useEffect(() => {
    const c = tempRef.current;
    if (!c || !hourly || !hourly.length) return;
    drawTempCurve(c, hourly, brand);
  }, [hourly, brand]);

  useEffect(() => {
    const c = popRef.current;
    if (!c || !hourly || !hourly.length) return;
    drawPopBars(c, hourly, brand);
  }, [hourly, brand]);

  if (!hourly || hourly.length === 0) return null;

  return (
    <div style={{ padding: `0 ${density.pad}px ${density.gap}px` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>Temperature · next 12 h</span>
        <span style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont }}>{hourly[hourly.length - 1].time}</span>
      </div>
      <canvas ref={tempRef} style={{ width: '100%', height: 60, display: 'block' }}/>
      <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 8 }}>
        Precip chance
      </div>
      <canvas ref={popRef} style={{ width: '100%', height: 36, display: 'block', marginTop: 4 }}/>
    </div>
  );
}

function drawTempCurve(canvas, hourly, brand) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);

  const temps = hourly.map((h) => h.temp);
  const min = Math.min(...temps);
  const max = Math.max(...temps);
  const span = Math.max(1, max - min);
  const pad = 12;
  const innerH = H - pad * 2;
  const step = W / Math.max(1, hourly.length - 1);

  // Filled gradient under curve
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, brand.accent + '55');
  grad.addColorStop(1, brand.accent + '00');
  ctx.beginPath();
  hourly.forEach((h, i) => {
    const x = i * step;
    const y = pad + (1 - (h.temp - min) / span) * innerH;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.lineTo(W, H);
  ctx.lineTo(0, H);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Line
  ctx.beginPath();
  hourly.forEach((h, i) => {
    const x = i * step;
    const y = pad + (1 - (h.temp - min) / span) * innerH;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.strokeStyle = brand.accent;
  ctx.lineWidth = 1.8;
  ctx.lineJoin = 'round';
  ctx.stroke();

  // Endpoints + labels
  ctx.font = `bold 11px ${brand.eyebrowFont}`;
  ctx.fillStyle = brand.text;
  hourly.forEach((h, i) => {
    if (i !== 0 && i !== hourly.length - 1 && h.temp !== max && h.temp !== min) return;
    const x = i * step;
    const y = pad + (1 - (h.temp - min) / span) * innerH;
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fillStyle = brand.accent;
    ctx.fill();
    ctx.fillStyle = brand.text;
    const label = `${h.temp}°`;
    const m = ctx.measureText(label);
    const lx = Math.min(Math.max(2, x - m.width / 2), W - m.width - 2);
    ctx.fillText(label, lx, y - 6);
  });
}

function drawPopBars(canvas, hourly, brand) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);

  const gap = 2;
  const barW = (W - (hourly.length - 1) * gap) / hourly.length;
  hourly.forEach((h, i) => {
    const x = i * (barW + gap);
    const pop = Math.max(0, Math.min(100, h.pop || 0));
    const barH = (pop / 100) * H;
    const grad = ctx.createLinearGradient(0, H - barH, 0, H);
    grad.addColorStop(0, '#60a5fa');
    grad.addColorStop(1, brand.accentDeep);
    ctx.fillStyle = pop > 0 ? grad : brand.line;
    if (pop > 0) {
      ctx.fillRect(x, H - barH, barW, barH);
    } else {
      ctx.fillRect(x, H - 2, barW, 2);
    }
  });
}
